/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import { useEffect } from 'react';

// react-router-dom components
import { useLocation, NavLink } from 'react-router-dom';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// import prism logo for navbar
import prismLogo from "assets/images/prism_logo_transparent_bg_x4.png";

// @mui material components
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';

// Vision UI Dashboard PRO custom icons

// Custom styles for the Sidenav
import SidenavRoot from 'examples/Sidenav/SidenavRoot';
import sidenavLogoLabel from 'examples/Sidenav/styles/sidenav';

// Scrollbar component
import { Scrollbars } from 'react-custom-scrollbars-2';

// Scrollbar configs
import { renderThumb, renderTrack, renderTrackRTL, renderView, renderViewRTL } from 'examples/Scrollbar';

// Vision UI Dashboard PRO React context
import { useVisionUIController, setMiniSidenav, setTransparentSidenav } from 'context';

// React icons
import { IoHome, IoDocument, IoCheckboxOutline, IoGlobeOutline, IoPerson, IoFlash, IoCreateOutline } from 'react-icons/io5';

function Sidenav({ color, brand, brandName, routes, ...rest }) {
	const [ controller, dispatch ] = useVisionUIController();
	const { miniSidenav, transparentSidenav } = controller;
	const location = useLocation();
	const { pathname } = location;

	const closeSidenav = () => setMiniSidenav(dispatch, true);

	useEffect(
		() => {
			// A function that sets the mini state of the sidenav.
			function handleMiniSidenav() {
				setMiniSidenav(dispatch, window.innerWidth < 1200);
			}

			/** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
			window.addEventListener('resize', handleMiniSidenav);

			// Call the handleMiniSidenav function to set the state with the initial value.
			handleMiniSidenav();

			// Remove event listener on cleanup
			return () => window.removeEventListener('resize', handleMiniSidenav);
		},
		[ dispatch, location ]
	);

	useEffect(() => {
		if (window.innerWidth < 1440) {
			setTransparentSidenav(dispatch, false);
		}
	}, []);

	// Simplified menu items configuration
	const menuItems = [
		{ name: 'Overview', icon: <IoHome size="18px" />, path: '/dashboards/overview' },
		{ name: 'Takedowns', icon: <IoDocument size="18px" />, path: '/dashboards/takedowns' },
		{ name: 'Review Content', icon: <IoCheckboxOutline size="18px" />, path: '/applications/reviewcontent' },
		{ name: 'Platform Tracker', icon: <IoGlobeOutline size="18px" />, path: '/dashboards/platforms' },
		{ name: 'Threat Actor', icon: <IoPerson size="18px" />, path: '/dashboards/threats' },
		{ name: 'Weekly Report', icon: <IoFlash size="18px" />, path: '/dashboards/reports' },
		{ name: 'Submit Content', icon: <IoCreateOutline size="18px" />, path: '/applications/submit' },
	];

	return (
		<SidenavRoot {...rest} variant='permanent' ownerState={{ miniSidenav, transparentSidenav }}>
			<Scrollbars
				autoHide
				autoHideTimeout={1000}
				renderThumbVertical={renderThumb}
				renderTrackVertical={window.location.href.includes('rtl') ? renderTrackRTL : renderTrack}
				renderView={window.location.href.includes('rtl') ? renderViewRTL : renderView}>
				<VuiBox
					display='flex'
					sx={{ flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
					<VuiBox>
						{/* Close button for mobile */}
						<VuiBox
							display={{ xs: 'block', xl: 'none' }}
							position='absolute'
							top={0}
							right={0}
							p={1.625}
							onClick={closeSidenav}
							sx={{ cursor: 'pointer' }}>
							<VuiTypography variant='h6' color='white'>
								<Icon sx={{ fontWeight: 'bold' }}>close</Icon>
							</VuiTypography>
						</VuiBox>

						{/* Logo */}
						<VuiBox pt={3} pb={2} px={2} textAlign='center'>
							<VuiBox component={NavLink} to='/' display='flex' alignItems='center' justifyContent='center'>
								<VuiBox
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1
									}}>
									<VuiBox
										display='flex'
										sx={{
											flexShrink: 0
										}}>
										<img 
											src={prismLogo} 
											alt="PRISM Logo" 
											style={{ width: '50px', height: '50px' }}
										/>
									</VuiBox>
									
									<VuiTypography
										variant='button'
										textGradient={true}
										color='white'
										fontSize='1.25rem'
										letterSpacing={2}
										fontWeight='medium'
										sx={{
											opacity: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : 1,
											maxWidth: miniSidenav || (miniSidenav && transparentSidenav) ? 0 : '100%',
											whiteSpace: 'nowrap',
											background: 'linear-gradient(to right, #ffffff,rgb(135, 135, 135))',
											WebkitBackgroundClip: 'text !important',
											WebkitTextFillColor: 'transparent !important'
										}}>
										{brandName}
									</VuiTypography>
									
								</VuiBox>
							</VuiBox>
						</VuiBox>

						{/* User Dropdown 
						<VuiBox px={2} mb={2} sx={{ maxWidth: '90%' }}>
							<Select
								value="LAST, FIRST"
								displayEmpty
								fullWidth
								sx={{
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgba(255, 255, 255, 0.2)',
										borderRadius: '12px',
									},
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgba(255, 255, 255, 0.4)',
									},
									'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										borderColor: '#0075FF',
									},
									color: 'white',
									fontSize: '12px',
									height: '40px',
									'& .MuiSelect-select': {
										paddingLeft: '12px',
										paddingRight: '32px',
									},
									'& .MuiSelect-icon': {
										color: 'white',
										right: '8px',
									},
								}}>
								<MenuItem value="LAST, FIRST" >CLIENT NAME</MenuItem>
							</Select>
						</VuiBox>
						*/}

						<Divider light sx={{ mx: 2, mb: 2 }} />

						{/* Menu Label */}
						{/*<VuiBox px={3} mb={2}>
							<VuiTypography
								variant='caption'
								color='white'
								fontWeight='regular'
								textTransform='uppercase'
								opacity={0.6}>
								Menu
							</VuiTypography>
						</VuiBox>*/}

						{/* Menu Items */}
						<List sx={{ px: 2 }}>
							{menuItems.map((item) => {
								const isActive = pathname === item.path;
								return (
									<VuiBox key={item.name} mb={1}>
										<VuiBox
											component={NavLink}
											to={item.path}
											sx={{
												display: 'flex',
												alignItems: 'center',
												padding: '10px 12px',
												borderRadius: '12px',
												backgroundColor: isActive ? 'rgba(0, 117, 255, 0.15)' : 'transparent',
												cursor: 'pointer',
												textDecoration: 'none',
												transition: 'all 0.2s ease-in-out',
												'&:hover': {
													backgroundColor: isActive
														? 'rgba(0, 117, 255, 0.15)'
														: 'rgba(255, 255, 255, 0.05)',
												},
											}}>
											<VuiBox
												sx={{
													color: isActive ? '#0075FF' : 'white',
													display: 'flex',
													alignItems: 'center',
													mr: 1.5,
												}}>
												{item.icon}
											</VuiBox>
											<VuiTypography
												variant='button'
												color='white'
												fontWeight={isActive ? 'medium' : 'regular'}
												sx={{
													opacity: miniSidenav ? 0 : 1,
													transition: 'opacity 0.2s ease-in-out',
												}}>
												{item.name}
											</VuiTypography>
										</VuiBox>
									</VuiBox>
								);
							})}
						</List>
					</VuiBox>

					{/* Footer copyright */}
					<VuiBox px={2} pb={2}>
						<VuiTypography variant='caption' color='text' textAlign='center'>
							© 2025
						</VuiTypography>
					</VuiBox>
				</VuiBox>
			</Scrollbars>
		</SidenavRoot>
	);
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
	color: 'info',
	brand: ''
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
	color: PropTypes.oneOf([ 'primary', 'secondary', 'info', 'success', 'warning', 'error', 'dark' ]),
	brand: PropTypes.string,
	brandName: PropTypes.string.isRequired,
	routes: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default Sidenav;